import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { environment } from 'src/environments/environment';
import { DataRest, ExportInfoListData, FileTypeData, FinalUsersListData, PaginatedFinalUsersList, PaginatedUserFiles, TextData, UserFileData } from '../model/api/data-rest';
import { UserFile } from '../model/user-file';
import { resources } from './api';
import { PaginationParams, toHttpParams } from '../model/api/pagination-params';
import { Observable } from 'rxjs';
import { ExportInfo, ImportInfo } from '../model/export';

@Injectable({
	providedIn: 'root'
})
export class UserFileService {
	constructor(
		private http: HttpClient
	) {
	}

	getUserFilesTypes(): Observable<FileTypeData> {
		return this.http.get<FileTypeData>(`${environment.apiUrl}${resources.get('userFiles')}${resources.get('types')}`);
	}

	getUserFilesList(
		type?: number,
		userId?: number,
		siteId?: number,
		noPagination?: boolean,
		paging?: PaginationParams
	): Observable<PaginatedUserFiles> {
		let params: HttpParams;
		if (paging) {
			params = toHttpParams(paging);
		} else {
			params = new HttpParams();
		}
		if (typeof type !== 'undefined') {
			params = params.set('type', type.toString());
		}
		if (userId) {
			params = params.set('userid', userId.toString());
		}
		if (siteId) {
			params = params.set('siteid', siteId.toString());
		}
		if (noPagination) {
			params = params.set('nopagination', '');
		}
		return this.http.get<PaginatedUserFiles>(`${environment.apiUrl}${resources.get('userFiles')}`, { params });
	}

	insertUserFile(file: UserFile, userId?: number): Observable<UserFileData> {
		let params = new HttpParams();
		if (userId) {
			params = params.set('userid', userId.toString());
		}
		return this.http.post<UserFileData>(`${environment.apiUrl}${resources.get('userFiles')}`, file, { params });
	}

	getUserFileContent(fileId: string, userId?: number): Observable<string> {
		let params = new HttpParams();
		if (userId) {
			params = params.set('userid', userId.toString());
		}
		return this.http.get(
			`${environment.apiUrl}${resources.get('userFiles')}/${fileId}${resources.get('download')}`,
			{ params, responseType: 'text' }
		);
	}

	deleteUserFile(fileId: string, userId?: number): Observable<DataRest> {
		let params = new HttpParams();
		if (userId) {
			params = params.set('userid', userId.toString());
		}
		return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('userFiles')}/${fileId}`, { params });
	}

	exportFinalUsersList(deviceId: number, name: string, userId: number): Observable<TextData> {
		if (userId !== undefined) {
			let params = new HttpParams();
			if (userId) {
				params = params.set('userid', userId.toString());
			}
			return this.http.post<TextData>(
				`${environment.apiUrl}${resources.get('v1')}${resources.get('devices')}/${deviceId}${resources.get('finalusers')}${resources.get('export')}`,
				{ Name: name }, { params }
			);
		}
		return this.http.post<TextData>(
			`${environment.apiUrl}${resources.get('v1')}${resources.get('devices')}/${deviceId}${resources.get('finalusers')}${resources.get('export')}`,
			{ Name: name }
		);
	}

	getFinalUsersLists(
		categoryId?: number,
		noPagination?: boolean,
		paging?: PaginationParams, userId?: number,
	): Observable<PaginatedFinalUsersList> {
		let params: HttpParams;
		if (paging) {
			params = toHttpParams(paging);
		} else {
			params = new HttpParams();
		}
		if (categoryId) {
			params = params.set('categoryid', categoryId);
		}
		if (noPagination) {
			params = params.set('nopagination', '');
		}
		if (userId) {
			params = params.set('userid', userId.toString());
		}
		return this.http.get<PaginatedFinalUsersList>(
			`${environment.apiUrl}${resources.get('v1')}${resources.get('devices')}${resources.get('finalusers')}${resources.get('list')}`,
			{ params }
		);
	}

	editFinalUsersList(exportId: number, name: string, userId?: number,): Observable<FinalUsersListData> {
		let params = new HttpParams();
		if (userId) {
			params = params.set('userid', userId.toString());
		}
		return this.http.patch<FinalUsersListData>(
			`${environment.apiUrl}${resources.get('v1')}${resources.get('devices')}${resources.get('finalusers')}/${exportId}`,
			{ Name: name }, { params }
		);
	}

	deleteFinalUsersList(
		exportId?: number, userId?: number,
	): Observable<DataRest> {
		let params = new HttpParams();
		if (userId) {
			params = params.set('userid', userId.toString());
		}
		return this.http.delete<DataRest>(
			`${environment.apiUrl}${resources.get('v1')}${resources.get('devices')}${resources.get('finalusers')}/${exportId}`, { params }
		);
	}

	importFinalUsersList(deviceId: number, exportId: number, userId?: number,): Observable<TextData> {
		let params = new HttpParams();
		if (userId) {
			params = params.set('userid', userId.toString());
		}
		return this.http.post<TextData>(
			`${environment.apiUrl}${resources.get('v1')}${resources.get('devices')}/${deviceId}${resources.get('finalusers')}/${exportId}${resources.get('import')}`,
			{}, { params }
		);
	}

	deleteFinalUsers(deviceId: number, userId?: number,): Observable<TextData> {
		let params = new HttpParams();
		if (userId) {
			params = params.set('userid', userId.toString());
		}
		return this.http.delete<TextData>(
			`${environment.apiUrl}${resources.get('v1')}${resources.get('devices')}/${deviceId}${resources.get('finalusers')}`,
			{ params }
		);
	}


	//-----------------------EXPORT DEVICE

	getExportDeviceList(userId: number, isImpersMode: boolean,
		noPagination?: boolean,
		paging?: PaginationParams
	): Observable<ExportInfoListData> {
		let params: HttpParams;
		if (paging) {
			params = toHttpParams(paging);
		} else {
			params = new HttpParams();
		}
		if (noPagination) {
			params = params.set('nopagination', '');
		}
		if (isImpersMode) {
			params = params.set('userid', userId);
		}
		return this.http.get<ExportInfoListData>(`${environment.apiUrl}${resources.get('automations')}${resources.get('exports')}`, { params });
	}

	exportDevice(deviceId: number, exportInfo: ExportInfo): Observable<DataRest> {
		const params = new HttpParams();
		return this.http.post<TextData>(
			`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('exports')}`,
			exportInfo, { params }
		);
	}

	updateExportDevice(exportId: number, exportInfo: ExportInfo): Observable<DataRest> {
		const params = new HttpParams();
		return this.http.patch<TextData>(
			`${environment.apiUrl}${resources.get('automations')}${resources.get('exports')}/${exportId}`,
			exportInfo, { params }
		);
	}


	deleteExportDevice(exportId: number): Observable<DataRest> {
		const params = new HttpParams();
		return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('automations')}${resources.get('exports')}/${exportId}`, { params });
	}


	importDevice(deviceId: number, exportId: number, importInfo: ImportInfo): Observable<DataRest> {
		const params = new HttpParams();
		return this.http.post<TextData>(
			`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('exports')}/${exportId}${resources.get('import')}`,
			importInfo, { params }
		);
	}

	//-----------------------EXPORT DEVICE


	//-----------------------EXPORT DEVICE MTMA

	getMtmaExportDeviceList(userId: number, isImpersMode: boolean,
		noPagination?: boolean,
		paging?: PaginationParams
	): Observable<ExportInfoListData> {
		let params: HttpParams;
		if (paging) {
			params = toHttpParams(paging);
		} else {
			params = new HttpParams();
		}
		if (isImpersMode) {
			params = params.set('userId', userId);
		}
		if (noPagination) {
			params = params.set('nopagination', '');
		}
		return this.http.get<ExportInfoListData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}${resources.get('exports')}`, { params });
	}

	exportMtmaDevice(deviceId: number, userId: number, exportInfo: ExportInfo, isImpersMode: boolean): Observable<DataRest> {
		let params = new HttpParams();
		if (isImpersMode) {
			params = params.set('userId', userId);
		}
		return this.http.post<TextData>(
			`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('export')}`,
			exportInfo, { params }
		);
	}

	updateMtmaExportDevice(exportId: number, exportInfo: ExportInfo, userId: number, isImpersMode: boolean): Observable<DataRest> {
		let params = new HttpParams();
		if (isImpersMode) {
			params = params.set('userId', userId);
		}
		return this.http.patch<TextData>(
			`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}${resources.get('exports')}/${exportId}`,
			exportInfo, { params }
		);
	}


	deleteMtmaExportDevice(exportId: number, userId: number, isImpersMode: boolean): Observable<DataRest> {
		let params = new HttpParams();
		if (isImpersMode) {
			params = params.set('userId', userId);
		}
		return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}${resources.get('exports')}/${exportId}`, { params });
	}


	importMtmaDevice(deviceId: number, exportId: number, importInfo: ImportInfo, userId: number, isImpersMode: boolean): Observable<DataRest> {
		let params = new HttpParams();
		if (isImpersMode) {
			params = params.set('userId', userId);
		}
		return this.http.post<TextData>(
			`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('exports')}/${exportId}${resources.get('import')}`,
			importInfo, { params }
		);
	}

	//-----------------------EXPORT DEVICE MTMA
}
